// src/utils/jobEstimatePriorityData.js
//
// Priority-specific data rules for the Job Estimate Spreadsheet.
//
// The spreadsheet was built for Servicing, where every recurring visit was its
// own record. Priority keeps a recurring job as ONE doc and expands its visits
// at read time (getOccurrencesForJob + occurrenceOverrides + excludedDates), and
// bills each visit with an approvedInvoices doc. These helpers make the
// spreadsheet see exactly the same visits, invoices, payments and Tech Daily Pay
// as Calendar / Home / Approved Invoices / Tech Daily Pay, without counting any
// dollar twice.
//
// Pure functions only - no Firestore reads or writes here.

import moment from "moment";
import { getOccurrencesForJob } from "./getOccurrencesForJob";
import {
  isRecurring,
  toDateKey as priorityDateKey,
  makeOccurrenceDateMatcher,
  getOverrideScheduledKey,
  getCanonicalKeyForScheduledKey,
} from "./recurringOccurrenceUtils";
import { dayJobCosts, UNASSIGNED_JOB, numberValue, roundMoney } from "./techDailyPay";
import { allocateDepositsToInvoices } from "./depositsInvoiceMatching";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** "YYYY-MM-DD" from a string, Date or Firestore Timestamp (local time). */
export const dateKeyOf = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const key = value.trim().slice(0, 10);
    return DATE_RE.test(key) ? key : "";
  }
  return priorityDateKey(value);
};

export const localTodayKey = () => moment().format("YYYY-MM-DD");
export const localMonthStartKey = () => moment().startOf("month").format("YYYY-MM-DD");

// ---------------------------------------------------------------------------
// VISITS - the same visits Calendar / Home show
// ---------------------------------------------------------------------------

/**
 * Every visit of a recurring job whose SCHEDULED day lands in [startKey, endKey].
 *   canonicalKey  the date the recurrence rule produced (what completedDates,
 *                 invoicedDates, excludedDates and occurrenceOverrides use)
 *   scheduledKey  the day it actually happens (after "Edit this job only")
 * Excluded (deleted) visits are dropped under either key.
 */
export const getRecurringVisits = (job, startKey, endKey) => {
  if (!job || !isRecurring(job)) return [];
  const seriesStart = dateKeyOf(job.startDate);
  if (!seriesStart) return [];

  const rangeStart = startKey || seriesStart;
  const rangeEnd = endKey || moment().add(6, "months").format("YYYY-MM-DD");
  if (rangeStart > rangeEnd) return [];

  const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates);

  // Canonical dates from the rule, plus every edited visit (a visit moved INTO
  // the range from a canonical date outside it still counts). The scheduled
  // day decides whether a visit is in range.
  const canonical = new Set(
    getOccurrencesForJob(
      { ...job, startDate: seriesStart },
      rangeStart,
      rangeEnd
    )
  );
  Object.keys(job.occurrenceOverrides || {}).forEach((key) => {
    const clean = dateKeyOf(key);
    if (clean && clean >= seriesStart) canonical.add(clean);
  });

  return Array.from(canonical)
    .map((canonicalKey) => ({
      canonicalKey,
      scheduledKey: getOverrideScheduledKey(job, canonicalKey) || canonicalKey,
    }))
    .filter(
      ({ canonicalKey, scheduledKey }) =>
        scheduledKey >= rangeStart && scheduledKey <= rangeEnd && !isExcluded(canonicalKey)
    )
    .sort(
      (a, b) =>
        a.scheduledKey.localeCompare(b.scheduledKey) ||
        a.canonicalKey.localeCompare(b.canonicalKey)
    );
};

/** Only the fields of a Priority "Edit this job only" snapshot that describe the visit. */
export const VISIT_OVERRIDE_FIELDS = [
  "techAssign",
  "estimatedTotal",
  "recurringInvoiceAmount",
  "scope",
  "serviceType",
  "estimatedMaterial",
];

export const pickVisitOverride = (job, canonicalKey) => {
  const snapshot = job?.occurrenceOverrides?.[canonicalKey];
  if (!snapshot || typeof snapshot !== "object") return {};
  const out = {};
  VISIT_OVERRIDE_FIELDS.forEach((field) => {
    if (snapshot[field] !== undefined && snapshot[field] !== null && snapshot[field] !== "") {
      out[field] = snapshot[field];
    }
  });
  return out;
};

/**
 * Default price of one visit when nothing else says otherwise:
 *   the first visit of the series uses Initial Amount (when set),
 *   every other visit uses the per-visit fee (recurringInvoiceAmount, which
 *   Scheduling keeps equal to the job's Job Total / estimatedTotal).
 */
export const defaultVisitPrice = (job, canonicalKey, visitOverride = {}) => {
  if (visitOverride.recurringInvoiceAmount !== undefined) {
    return numberValue(visitOverride.recurringInvoiceAmount);
  }
  if (visitOverride.estimatedTotal !== undefined) return numberValue(visitOverride.estimatedTotal);
  const firstKey = dateKeyOf(job?.seriesStartDate || job?.originalSeriesStartDate || job?.startDate);
  if (canonicalKey && firstKey && canonicalKey === firstKey && numberValue(job?.initialAmount) > 0) {
    return numberValue(job.initialAmount);
  }
  const perVisit = numberValue(job?.recurringInvoiceAmount);
  return perVisit > 0 ? perVisit : numberValue(job?.estimatedTotal);
};

/** Per-visit status from the job doc (completedDates / invoicedDates). */
export const visitStatusFromJob = (job, canonicalKey) => {
  if (makeOccurrenceDateMatcher(job, job?.invoicedDates)(canonicalKey)) return "Invoice Sent";
  if (makeOccurrenceDateMatcher(job, job?.completedDates)(canonicalKey)) return "Completed";
  return "Active";
};

// ---------------------------------------------------------------------------
// APPROVED INVOICES - one invoice belongs to exactly one row
// ---------------------------------------------------------------------------

export const invoiceTotal = (invoice = {}) => {
  const direct = numberValue(invoice.amount ?? invoice.price ?? invoice.rate ?? invoice.total);
  if (direct) return roundMoney(direct);
  const rows = Array.isArray(invoice.serviceRows) ? invoice.serviceRows : [];
  return roundMoney(
    rows.reduce((sum, row) => sum + numberValue(row.total ?? row.amount), 0)
  );
};

/** Same invoice number Priority's Approved Invoices page uses. */
export const invoiceNumberOf = (invoice = {}) =>
  String(invoice.wo || invoice.invoiceNumber || invoice.id || "").trim();

export const invoiceStatusOf = (invoice = {}) => {
  const raw = String(invoice.status || "").trim().toLowerCase();
  if (invoice.paid === true || raw === "paid") return "Paid";
  if (raw === "sent" || invoice.sent === true || invoice.sentAt || invoice.emailedAt) return "Sent";
  if (raw === "approved") return "Approved";
  return invoice.status || "Draft";
};

export const invoiceServiceKey = (invoice = {}) =>
  dateKeyOf(
    invoice.dateOfService ||
      invoice.serviceDate ||
      invoice.invoiceDate ||
      invoice.completedAt ||
      invoice.createdAt
  );

export const rowKeyForJob = (jobId) => `job:${jobId}`;
export const rowKeyForVisit = (jobId, canonicalKey) => `occ:${jobId}|${canonicalKey}`;

const splitOccurrenceDocId = (value) => {
  const match = /^(.*?)(?:__|_)(\d{4}-\d{2}-\d{2})$/.exec(String(value || ""));
  return match ? { jobId: match[1], dateKey: match[2] } : null;
};

/**
 * Assigns every approvedInvoices doc to ONE row:
 *   recurring job -> the visit (jobId + occurrenceKey, then
 *                    approvedInvoiceIdByDate, then the service date)
 *   one-time job  -> the job
 * Match order: jobId / originalJobId, then WO / invoice number.
 * Returns { byRow: { rowKey: invoice[] }, rowOfInvoice: { invoiceId: rowKey },
 *           unmatched: invoice[] }.
 */
export const matchInvoicesToRows = (invoices = [], jobs = []) => {
  const jobsById = {};
  const jobsByWo = {};
  jobs.forEach((job) => {
    if (!job?.id) return;
    jobsById[job.id] = job;
    const wo = String(job.wo || "").trim();
    if (wo && !jobsByWo[wo]) jobsByWo[wo] = job;
  });

  // approvedInvoiceIdByDate: { canonicalDate: invoiceId } written on completion.
  const dateByInvoiceId = {};
  jobs.forEach((job) => {
    Object.entries(job?.approvedInvoiceIdByDate || {}).forEach(([dateKey, invoiceId]) => {
      if (invoiceId) dateByInvoiceId[invoiceId] = { jobId: job.id, dateKey: dateKeyOf(dateKey) };
    });
  });

  const byRow = {};
  const rowOfInvoice = {};
  const unmatched = [];

  invoices.forEach((invoice) => {
    if (!invoice?.id) return;
    let job = null;
    let hintedDate = "";

    [invoice.originalJobId, invoice.jobId].filter(Boolean).some((ref) => {
      if (jobsById[ref]) {
        job = jobsById[ref];
        return true;
      }
      const split = splitOccurrenceDocId(ref);
      if (split && jobsById[split.jobId]) {
        job = jobsById[split.jobId];
        hintedDate = split.dateKey;
        return true;
      }
      return false;
    });

    const byIdDate = dateByInvoiceId[invoice.id];
    if (!job && byIdDate && jobsById[byIdDate.jobId]) job = jobsById[byIdDate.jobId];
    if (!job) job = jobsByWo[String(invoice.wo || "").trim()] || jobsByWo[invoiceNumberOf(invoice)] || null;

    if (!job) {
      unmatched.push(invoice);
      return;
    }

    let rowKey = rowKeyForJob(job.id);
    if (isRecurring(job)) {
      let canonical =
        (byIdDate && byIdDate.jobId === job.id && byIdDate.dateKey) ||
        dateKeyOf(invoice.occurrenceKey) ||
        hintedDate ||
        dateKeyOf(invoice.originalOccurrenceDate || invoice.occurrenceDate) ||
        invoiceServiceKey(invoice);
      if (canonical) {
        canonical = getCanonicalKeyForScheduledKey(job, canonical) || canonical;
        rowKey = rowKeyForVisit(job.id, canonical);
      }
    }

    if (!byRow[rowKey]) byRow[rowKey] = [];
    byRow[rowKey].push(invoice);
    rowOfInvoice[invoice.id] = rowKey;
  });

  return { byRow, rowOfInvoice, unmatched };
};

// ---------------------------------------------------------------------------
// DEPOSITS on recurring jobs
// ---------------------------------------------------------------------------
//
// Every visit of a recurring job shares the job's WO, so how much of each
// visit's invoice is paid comes from the ONE shared rule in
// utils/depositsInvoiceMatching (explicit invoice link, then visit link, then
// oldest visit first). This page only regroups those per-invoice amounts by
// row; it never applies money on its own.

const isInvoiceOnlyDeposit = (deposit = {}) =>
  deposit.invoiceOnly === true || deposit.splitInvoiceOnly === true;

/** Which visit (canonical date) a deposit was recorded against, or "". */
export const depositVisitKey = (deposit = {}, job = {}) => {
  const jobRef = String(deposit.originalJobId || deposit.jobId || "");
  const direct = dateKeyOf(deposit.occurrenceKey);
  if (direct && (!jobRef || jobRef === job.id)) {
    return getCanonicalKeyForScheduledKey(job, direct) || direct;
  }
  const invoiceNumber = String(deposit.invoiceNumber || "").trim();
  const split = splitOccurrenceDocId(invoiceNumber);
  if (split && split.jobId === job.id) return split.dateKey;
  const wo = String(job.wo || "").trim();
  if (wo && invoiceNumber.startsWith(`${wo}-`)) {
    const rest = invoiceNumber.slice(wo.length + 1);
    if (DATE_RE.test(rest)) return rest;
  }
  return "";
};

/**
 * Per-row view of what customers paid on recurring jobs, built ONLY from the
 * shared paid-per-invoice rule (utils/depositsInvoiceMatching via
 * computeInvoicePaidTotals) so this page shows exactly what Approved Invoices
 * and Deposits mark as paid.
 *
 * @param paidTotals  { invoiceId: amountPaid } from computeInvoicePaidTotals
 * @returns {{
 *   paidByRow:      { rowKey: number },     sum of that visit's invoices' paid
 *   directByRow:    { rowKey: deposit[] },  deposit docs recorded on that visit
 *                                           (listed so they can be edited; their
 *                                           money is already inside paidByRow)
 *   unappliedByJob: { jobId: number }       paid on the job but on no invoice
 * }}
 */
export const recurringPaymentsByRow = (
  deposits = [],
  jobs = [],
  invoiceMatch = { byRow: {}, rowOfInvoice: {} },
  paidTotals = {}
) => {
  const paidByRow = {};
  const directByRow = {};
  const unappliedByJob = {};

  const recurringJobs = jobs.filter((job) => job?.id && isRecurring(job));
  const recurringById = {};
  recurringJobs.forEach((job) => {
    recurringById[job.id] = job;
  });

  // Paid per visit row = sum of its invoices' paid amounts.
  Object.entries(invoiceMatch.byRow || {}).forEach(([rowKey, invoices]) => {
    if (!rowKey.startsWith("occ:")) return;
    paidByRow[rowKey] = roundMoney(
      invoices.reduce((sum, inv) => sum + numberValue(paidTotals[inv.id]), 0)
    );
  });

  // Money received per recurring job (any deposit that points at it).
  const receivedByJob = {};
  deposits.forEach((deposit) => {
    if (!deposit || isInvoiceOnlyDeposit(deposit)) return;
    const amount = numberValue(deposit.amount);
    const invoiceNumber = String(deposit.invoiceNumber || "").trim();
    const jobRef = String(deposit.originalJobId || deposit.jobId || "");
    const split = splitOccurrenceDocId(invoiceNumber);

    let job = recurringById[jobRef] || (split && recurringById[split.jobId]) || null;
    if (!job) {
      job =
        recurringJobs.find((j) => {
          const wo = String(j.wo || "").trim();
          return wo && (invoiceNumber === wo || invoiceNumber.startsWith(`${wo}-`));
        }) || null;
    }
    if (!job) return;

    receivedByJob[job.id] = (receivedByJob[job.id] || 0) + amount;
    const visitKey = depositVisitKey(deposit, job);
    if (visitKey) {
      const rowKey = rowKeyForVisit(job.id, visitKey);
      if (!directByRow[rowKey]) directByRow[rowKey] = [];
      directByRow[rowKey].push(deposit);
    }
  });

  // Whatever the shared rule could not put on any of the job's invoices
  // (e.g. no visit invoiced yet) is shown once on the job's main row.
  recurringJobs.forEach((job) => {
    const onInvoices = Object.entries(invoiceMatch.rowOfInvoice || {})
      .filter(([, rowKey]) => rowKey.startsWith(`occ:${job.id}|`) || rowKey === `job:${job.id}`)
      .reduce((sum, [invoiceId]) => sum + numberValue(paidTotals[invoiceId]), 0);
    const left = roundMoney((receivedByJob[job.id] || 0) - onInvoices);
    if (left > 0.004) unappliedByJob[job.id] = left;
  });

  return { paidByRow, directByRow, unappliedByJob };
};

export const computeInvoicePaidTotals = (deposits = [], invoices = [], jobs = []) =>
  // ONE rule for "how much of this invoice is paid", shared with Deposits,
  // Approved Invoices and Cash Plan, so the pages never flip an invoice's
  // Paid status back and forth.
  allocateDepositsToInvoices(invoices, deposits);

// ---------------------------------------------------------------------------
// TECH DAILY PAY per job and per visit
// ---------------------------------------------------------------------------

/**
 * { jobId: { "YYYY-MM-DD": $ } } from saved dailyLabor days (range optional).
 * A job id saved as "<jobId>_<date>" is folded back onto its job.
 */
export const dailyPayByJobDate = (days = [], usersById = {}, startKey = "", endKey = "") => {
  const out = {};
  days.forEach((day) => {
    const date = String(day.date || day.id || "").slice(0, 10);
    if (!date) return;
    if (startKey && date < startKey) return;
    if (endKey && date > endKey) return;
    Object.entries(dayJobCosts(day, usersById)).forEach(([rawJobId, amount]) => {
      if (rawJobId === UNASSIGNED_JOB) return;
      const split = splitOccurrenceDocId(rawJobId);
      const jobId = split ? split.jobId : rawJobId;
      if (!out[jobId]) out[jobId] = {};
      out[jobId][date] = roundMoney((out[jobId][date] || 0) + amount);
    });
  });
  return out;
};

/**
 * Spread one recurring job's daily pay over its visits: a day goes to the visit
 * scheduled that day, else the last visit before it, else the first visit.
 * Returns { canonicalKey: $ }.
 */
export const attributeDailyPayToVisits = (dateAmounts = {}, visits = []) => {
  const out = {};
  if (!visits.length) return out;
  const sorted = [...visits].sort((a, b) => a.scheduledKey.localeCompare(b.scheduledKey));
  Object.entries(dateAmounts).forEach(([date, amount]) => {
    let target = sorted.find((v) => v.scheduledKey === date);
    if (!target) {
      const before = sorted.filter((v) => v.scheduledKey <= date);
      target = before.length ? before[before.length - 1] : sorted[0];
    }
    out[target.canonicalKey] = roundMoney((out[target.canonicalKey] || 0) + numberValue(amount));
  });
  return out;
};
